import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  CheckCircle2,
  Clock,
  GitBranch,
  ArrowUpCircle,
  Trash2,
  FileCode,
  TrendingDown,
  TrendingUp,
  Minus,
  RotateCcw,
  History,
  Activity,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { MockModelVersionHistory, getMockModelVersionHistory } from '@/mock/gpu-instance';
import { useTritonModelStore, ModelWithVersions } from '@/store/tritonModelStore';

interface VersionHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  modelName: string;
  instanceId: string;
}

const formatDate = (timestamp: number) => {
  return new Date(timestamp).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatRelativeTime = (timestamp: number) => {
  const diff = Date.now() - timestamp;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
};

const getLatencyTrend = (currentLatency: number, previousLatency: number) => {
  if (previousLatency === 0) return null;
  const percentChange = ((currentLatency - previousLatency) / previousLatency) * 100;
  if (Math.abs(percentChange) < 5) return { icon: Minus, color: 'text-gray-500', text: 'No change' };
  if (percentChange < 0) return { icon: TrendingDown, color: 'text-green-500', text: `${Math.abs(percentChange).toFixed(0)}% faster` };
  return { icon: TrendingUp, color: 'text-red-500', text: `${percentChange.toFixed(0)}% slower` };
};

const VersionHistoryDialog = ({
  open,
  onOpenChange,
  modelName,
  instanceId,
}: VersionHistoryDialogProps) => {
  const [selectedVersions, setSelectedVersions] = useState<string[]>([]);
  const [confirmRollbackOpen, setConfirmRollbackOpen] = useState(false);
  const [rollbackVersion, setRollbackVersion] = useState<string>('');
  const [isActivating, setIsActivating] = useState(false);

  // Get store actions
  const setActiveVersion = useTritonModelStore((state) => state.setActiveVersion);
  const getModelVersionHistory = useTritonModelStore((state) => state.getModelVersionHistory);
  const deleteModelVersion = useTritonModelStore((state) => state.deleteModelVersion);

  // Get version history - combine mock data with store data
  const versionHistory = useMemo(() => {
    const mockHistory = getMockModelVersionHistory(instanceId);
    const storeHistory = getModelVersionHistory(instanceId, modelName);

    // If model exists in mock data, use it
    if (mockHistory[modelName]) {
      return mockHistory[modelName];
    }

    // Otherwise, convert store history to mock format
    if (storeHistory) {
      return {
        name: storeHistory.name,
        versions: storeHistory.versions.map((v) => ({
          version: v.version,
          backend: v.backend,
          status: v.status,
          uploadedAt: v.uploadedAt,
          isActive: v.isActive,
          maxBatchSize: v.maxBatchSize,
          files: v.files,
          configPbtxt: v.configPbtxt,
          metrics: v.metrics,
          changelog: undefined,
        })),
        activeVersion: storeHistory.activeVersion,
      } as MockModelVersionHistory;
    }

    return null;
  }, [instanceId, modelName, getModelVersionHistory]);

  if (!versionHistory) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Version History</DialogTitle>
            <DialogDescription>No version history available for this model.</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  const sortedVersions = [...versionHistory.versions].sort(
    (a, b) => b.uploadedAt - a.uploadedAt
  );

  const handleRollback = (version: string) => {
    setRollbackVersion(version);
    setConfirmRollbackOpen(true);
  };

  const confirmRollback = async () => {
    setIsActivating(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 800));
    setActiveVersion(instanceId, modelName, rollbackVersion);
    setIsActivating(false);
    setConfirmRollbackOpen(false);
  };

  const toggleVersionSelection = (version: string) => {
    if (selectedVersions.includes(version)) {
      setSelectedVersions(selectedVersions.filter((v) => v !== version));
    } else if (selectedVersions.length < 2) {
      setSelectedVersions([...selectedVersions, version]);
    }
  };

  // Get versions for comparison
  const comparisonVersions = selectedVersions.length === 2
    ? sortedVersions.filter((v) => selectedVersions.includes(v.version))
    : null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Version History: {modelName}
            </DialogTitle>
            <DialogDescription>
              {sortedVersions.length} versions available. Active version: v{versionHistory.activeVersion}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="timeline" className="mt-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="timeline">
                <GitBranch className="h-4 w-4 mr-2" />
                Timeline
              </TabsTrigger>
              <TabsTrigger value="compare">
                <Activity className="h-4 w-4 mr-2" />
                Compare
              </TabsTrigger>
              <TabsTrigger value="metrics">
                <TrendingDown className="h-4 w-4 mr-2" />
                Metrics
              </TabsTrigger>
            </TabsList>

            {/* Timeline View */}
            <TabsContent value="timeline" className="mt-4">
              <ScrollArea className="h-[450px] pr-4">
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-[19px] top-0 bottom-0 w-0.5 bg-border" />

                  <div className="space-y-6">
                    {sortedVersions.map((version, index) => {
                      const previousVersion = sortedVersions[index + 1];
                      const latencyTrend = previousVersion
                        ? getLatencyTrend(version.metrics.avgLatencyMs, previousVersion.metrics.avgLatencyMs)
                        : null;

                      return (
                        <div key={version.version} className="relative flex gap-4">
                          {/* Timeline dot */}
                          <div
                            className={cn(
                              'relative z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 bg-background',
                              version.isActive
                                ? 'border-green-500 bg-green-500/10'
                                : 'border-muted-foreground/30'
                            )}
                          >
                            {version.isActive ? (
                              <CheckCircle2 className="h-5 w-5 text-green-500" />
                            ) : (
                              <span className="text-sm font-mono text-muted-foreground">
                                v{version.version}
                              </span>
                            )}
                          </div>

                          {/* Content */}
                          <div className="flex-1 pb-2">
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold">Version {version.version}</span>
                                  {version.isActive && (
                                    <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
                                      Active
                                    </Badge>
                                  )}
                                  <Badge variant="outline" className="text-xs">
                                    {version.backend}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                                  <Clock className="h-3 w-3" />
                                  <span>{formatDate(version.uploadedAt)}</span>
                                  <span className="text-xs">({formatRelativeTime(version.uploadedAt)})</span>
                                </div>
                              </div>

                              {!version.isActive && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleRollback(version.version)}
                                      >
                                        <RotateCcw className="h-4 w-4 mr-1" />
                                        Rollback
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Set this version as active</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>

                            {/* Changelog */}
                            {version.changelog && (
                              <p className="text-sm text-muted-foreground mt-2 italic">
                                "{version.changelog}"
                              </p>
                            )}

                            {/* Metrics summary */}
                            <div className="flex items-center gap-4 mt-3 text-sm">
                              <div className="flex items-center gap-1">
                                <span className="text-muted-foreground">Latency:</span>
                                <span className="font-mono">{version.metrics.avgLatencyMs}ms</span>
                                {latencyTrend && (
                                  <span className={cn('flex items-center gap-0.5', latencyTrend.color)}>
                                    <latencyTrend.icon className="h-3 w-3" />
                                    <span className="text-xs">{latencyTrend.text}</span>
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-muted-foreground">Batch:</span>
                                <span className="font-mono">{version.maxBatchSize}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-muted-foreground">Files:</span>
                                <span className="font-mono">{version.files.length}</span>
                              </div>
                            </div>

                            {/* Files */}
                            <div className="flex flex-wrap gap-1 mt-2">
                              {version.files.map((file) => (
                                <Badge key={file} variant="secondary" className="text-xs font-mono">
                                  <FileCode className="h-3 w-3 mr-1" />
                                  {file}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Compare View */}
            <TabsContent value="compare" className="mt-4">
              <div className="space-y-4">
                <div className="text-sm text-muted-foreground">
                  Select 2 versions to compare their configurations and metrics.
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {sortedVersions.map((version) => (
                    <div
                      key={version.version}
                      onClick={() => toggleVersionSelection(version.version)}
                      className={cn(
                        'p-3 border rounded-lg cursor-pointer transition-colors',
                        selectedVersions.includes(version.version)
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-semibold">v{version.version}</span>
                          {version.isActive && (
                            <Badge className="bg-green-500/10 text-green-600 text-xs">Active</Badge>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {formatRelativeTime(version.uploadedAt)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {comparisonVersions && (
                  <div className="mt-6 border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[150px]">Property</TableHead>
                          <TableHead>v{comparisonVersions[0].version}</TableHead>
                          <TableHead>v{comparisonVersions[1].version}</TableHead>
                          <TableHead className="w-[100px]">Change</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-medium">Backend</TableCell>
                          <TableCell>{comparisonVersions[0].backend}</TableCell>
                          <TableCell>{comparisonVersions[1].backend}</TableCell>
                          <TableCell>
                            {comparisonVersions[0].backend !== comparisonVersions[1].backend ? (
                              <Badge variant="outline" className="text-yellow-600">Changed</Badge>
                            ) : (
                              <span className="text-muted-foreground text-xs">Same</span>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium">Max Batch</TableCell>
                          <TableCell className="font-mono">{comparisonVersions[0].maxBatchSize}</TableCell>
                          <TableCell className="font-mono">{comparisonVersions[1].maxBatchSize}</TableCell>
                          <TableCell>
                            {comparisonVersions[0].maxBatchSize !== comparisonVersions[1].maxBatchSize ? (
                              <Badge
                                variant="outline"
                                className={cn(
                                  comparisonVersions[0].maxBatchSize > comparisonVersions[1].maxBatchSize
                                    ? 'text-green-600'
                                    : 'text-yellow-600'
                                )}
                              >
                                {comparisonVersions[0].maxBatchSize > comparisonVersions[1].maxBatchSize ? '+' : ''}
                                {comparisonVersions[0].maxBatchSize - comparisonVersions[1].maxBatchSize}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground text-xs">Same</span>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium">Avg Latency</TableCell>
                          <TableCell className="font-mono">{comparisonVersions[0].metrics.avgLatencyMs}ms</TableCell>
                          <TableCell className="font-mono">{comparisonVersions[1].metrics.avgLatencyMs}ms</TableCell>
                          <TableCell>
                            {comparisonVersions[0].metrics.avgLatencyMs !== comparisonVersions[1].metrics.avgLatencyMs ? (
                              <Badge
                                variant="outline"
                                className={cn(
                                  comparisonVersions[0].metrics.avgLatencyMs < comparisonVersions[1].metrics.avgLatencyMs
                                    ? 'text-green-600'
                                    : 'text-red-600'
                                )}
                              >
                                {comparisonVersions[0].metrics.avgLatencyMs < comparisonVersions[1].metrics.avgLatencyMs
                                  ? `${((comparisonVersions[1].metrics.avgLatencyMs - comparisonVersions[0].metrics.avgLatencyMs) / comparisonVersions[1].metrics.avgLatencyMs * 100).toFixed(0)}% faster`
                                  : `${((comparisonVersions[0].metrics.avgLatencyMs - comparisonVersions[1].metrics.avgLatencyMs) / comparisonVersions[1].metrics.avgLatencyMs * 100).toFixed(0)}% slower`
                                }
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground text-xs">Same</span>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium">P99 Latency</TableCell>
                          <TableCell className="font-mono">{comparisonVersions[0].metrics.p99LatencyMs}ms</TableCell>
                          <TableCell className="font-mono">{comparisonVersions[1].metrics.p99LatencyMs}ms</TableCell>
                          <TableCell>
                            {comparisonVersions[0].metrics.p99LatencyMs !== comparisonVersions[1].metrics.p99LatencyMs ? (
                              <Badge
                                variant="outline"
                                className={cn(
                                  comparisonVersions[0].metrics.p99LatencyMs < comparisonVersions[1].metrics.p99LatencyMs
                                    ? 'text-green-600'
                                    : 'text-red-600'
                                )}
                              >
                                {comparisonVersions[0].metrics.p99LatencyMs < comparisonVersions[1].metrics.p99LatencyMs
                                  ? `${((comparisonVersions[1].metrics.p99LatencyMs - comparisonVersions[0].metrics.p99LatencyMs) / comparisonVersions[1].metrics.p99LatencyMs * 100).toFixed(0)}% faster`
                                  : `${((comparisonVersions[0].metrics.p99LatencyMs - comparisonVersions[1].metrics.p99LatencyMs) / comparisonVersions[1].metrics.p99LatencyMs * 100).toFixed(0)}% slower`
                                }
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground text-xs">Same</span>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium">Inferences</TableCell>
                          <TableCell className="font-mono">{comparisonVersions[0].metrics.inferenceCount.toLocaleString()}</TableCell>
                          <TableCell className="font-mono">{comparisonVersions[1].metrics.inferenceCount.toLocaleString()}</TableCell>
                          <TableCell>
                            <span className="text-muted-foreground text-xs">-</span>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium">Files</TableCell>
                          <TableCell className="font-mono">{comparisonVersions[0].files.length}</TableCell>
                          <TableCell className="font-mono">{comparisonVersions[1].files.length}</TableCell>
                          <TableCell>
                            {comparisonVersions[0].files.length !== comparisonVersions[1].files.length ? (
                              <Badge variant="outline" className="text-yellow-600">
                                {comparisonVersions[0].files.length > comparisonVersions[1].files.length ? '+' : ''}
                                {comparisonVersions[0].files.length - comparisonVersions[1].files.length}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground text-xs">Same</span>
                            )}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                )}

                {selectedVersions.length > 0 && selectedVersions.length < 2 && (
                  <div className="text-center text-sm text-muted-foreground py-4">
                    Select one more version to compare
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Metrics View */}
            <TabsContent value="metrics" className="mt-4">
              <ScrollArea className="h-[450px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Version</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Avg Latency</TableHead>
                      <TableHead>P99 Latency</TableHead>
                      <TableHead>Queue Time</TableHead>
                      <TableHead>Inferences</TableHead>
                      <TableHead>Deployed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedVersions.map((version) => (
                      <TableRow key={version.version}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-semibold">v{version.version}</span>
                            {version.isActive && (
                              <Badge className="bg-green-500/10 text-green-600 text-xs">Active</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={cn(
                              version.status === 'ready' && 'text-green-600 border-green-500/30',
                              version.status === 'loading' && 'text-yellow-600 border-yellow-500/30',
                              version.status === 'error' && 'text-red-600 border-red-500/30'
                            )}
                          >
                            {version.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono">{version.metrics.avgLatencyMs}ms</TableCell>
                        <TableCell className="font-mono">{version.metrics.p99LatencyMs}ms</TableCell>
                        <TableCell className="font-mono">{version.metrics.queueTimeMs}ms</TableCell>
                        <TableCell className="font-mono">
                          {version.metrics.inferenceCount.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatRelativeTime(version.uploadedAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Rollback Confirmation Dialog */}
      <AlertDialog open={confirmRollbackOpen} onOpenChange={setConfirmRollbackOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5" />
              Confirm Rollback
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to rollback <strong>{modelName}</strong> to version{' '}
              <strong>v{rollbackVersion}</strong>? This will make it the active version for inference
              requests.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isActivating}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRollback} disabled={isActivating}>
              {isActivating ? (
                <>
                  <ArrowUpCircle className="h-4 w-4 mr-2 animate-spin" />
                  Activating...
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Rollback to v{rollbackVersion}
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default VersionHistoryDialog;
